import { base64ToFile } from "@/utils/conversion";

/**
 * Email verification is switched off while ZCT has no working mail credential, so the
 * "Verify Your Email" wizard step is skipped and registration submits from the business
 * step instead. Set VITE_EMAIL_VERIFICATION_ENABLED=true to restore it.
 *
 * Note the backend never validated the registration OTP in the first place — `createUser`
 * does not consult EmailVerification — so this removes a step that was already decorative.
 */
export const EMAIL_VERIFICATION_ENABLED =
    import.meta.env.VITE_EMAIL_VERIFICATION_ENABLED === "true";

type RegistrationStore = {
    personal?: any;
    identification?: any;
    business?: any;
};

/**
 * Build the multipart payload the registration endpoint expects from the wizard store.
 * Extracted so the verification step and the business step can both submit it.
 */
export async function buildRegistrationFormData(store: RegistrationStore): Promise<FormData> {
    const { personal, identification, business } = store;

    if (!personal || !identification || !business) {
        throw new Error("Registration details are incomplete");
    }

    const formData = new FormData();

    if (identification.avatarFile) {
        const imageFile = await base64ToFile(identification.avatarFile, "avatar-file");
        formData.append("profile", imageFile);
    }

    const idFile = await base64ToFile(identification.idFile, "id-file");

    formData.append("firstName", personal.firstName);
    formData.append("lastName", personal.lastName);
    formData.append("email", personal.email);
    formData.append("password", personal.password);
    formData.append("salutation", personal.salutation);
    formData.append("phoneNumber", personal.phone);

    if (identification.dateOfBirth) {
        formData.append("dateOfBirth", identification.dateOfBirth);
    }

    formData.append("nationality", identification.nationality);
    formData.append("identificationType", identification.idType);
    formData.append("identificationNumber", identification.idNumber);
    formData.append("identificationImages", idFile);
    formData.append("business.BusinessName", business.businessName);

    if (business.tradingName) {
        formData.append("business.businessTradingName", business.tradingName);
    }

    if (business.tinNumber) {
        formData.append("business.businessTinNumber", business.tinNumber.toString());
    }

    formData.append("business.businessRegion", business.region);
    formData.append("business.businessDistrict", business.district);

    if (business.street) {
        formData.append("business.businessStreet", business.street);
    }

    formData.append("business.businessManager", business.managerName);
    formData.append("business.businessEmail", business.managerEmail);
    formData.append("business.businessPhone", business.managerPhone);

    if (business.latitude) {
        formData.append("business.businessLat", business.latitude);
    }
    if (business.longitude) {
        formData.append("business.businessLng", business.longitude);
    }

    return formData;
}
